/**
 * RadarCard Component
 *
 * Compact employee summary card for lists, search results, and carousels.
 * Shows avatar, name, role, performance score, team size, and engagement.
 */
import React from 'react'
import { cn } from '@/lib/utils'
import { Star, Users } from 'lucide-react'
import { EngagementIndicator } from './EngagementIndicator'
import { PerformanceScoreInline } from './PerformanceScoreBadge'
import type { EmployeeSummary, EngagementSignal } from '@/types/radar'

interface RadarCardProps {
  employee: EmployeeSummary
  performanceScore: number | null
  teamSize: number
  engagementSignal: EngagementSignal
  variant?: 'compact' | 'card'
  onClick?: () => void
  onFavorite?: () => void
  isFavorite?: boolean
  className?: string
}

export function RadarCard({
  employee,
  performanceScore,
  teamSize,
  engagementSignal,
  variant = 'card',
  onClick,
  onFavorite,
  isFavorite = false,
  className,
}: RadarCardProps) {
  if (variant === 'compact') {
    return (
      <button
        onClick={onClick}
        className={cn(
          'flex items-center justify-between w-full p-2 rounded-lg',
          'hover:bg-brand-dark-light transition-colors text-left',
          className
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-brand-gray/30 flex items-center justify-center flex-shrink-0">
            {employee.pictureUrl ? (
              <img
                src={employee.pictureUrl}
                alt={employee.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-white">
                {employee.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-white font-medium truncate">{employee.name}</div>
            <div className="text-brand-gray text-xs truncate">{employee.role}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <PerformanceScoreInline score={performanceScore} />
          {teamSize > 0 && (
            <span className="text-brand-gray text-xs flex items-center gap-1">
              <Users className="w-3 h-3" />
              {teamSize}
            </span>
          )}
          {/* Hidden: engagement indicator - feature temporarily disabled
          <EngagementIndicator signal={engagementSignal} size="sm" />
          */}
        </div>
      </button>
    )
  }

  // Card variant
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative p-4 rounded-xl border border-brand-gray/20',
        'bg-brand-dark-light hover:bg-brand-dark-lighter',
        'transition-all duration-150 cursor-pointer',
        'hover:scale-[1.02] hover:shadow-lg',
        className
      )}
    >
      {/* Favorite button */}
      {onFavorite && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onFavorite()
          }}
          className={cn(
            'absolute top-2 right-2 p-1.5 rounded-full transition-colors',
            isFavorite
              ? 'text-yellow-400 bg-yellow-400/10'
              : 'text-brand-gray hover:text-yellow-400 hover:bg-yellow-400/10'
          )}
        >
          <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      )}

      {/* Avatar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-brand-gray/30 flex items-center justify-center flex-shrink-0">
          {employee.pictureUrl ? (
            <img
              src={employee.pictureUrl}
              alt={employee.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <span className="text-lg font-medium text-white">
              {employee.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate">{employee.name}</div>
          <div className="text-brand-gray text-sm truncate">{employee.role}</div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="flex items-center justify-between">
        <PerformanceScoreInline score={performanceScore} />
        <div className="flex items-center gap-2">
          {teamSize > 0 && (
            <span className="text-brand-gray text-sm flex items-center gap-1">
              <Users className="w-4 h-4" />
              {teamSize} rpts
            </span>
          )}
          {/* Hidden: engagement indicator - feature temporarily disabled
          <EngagementIndicator signal={engagementSignal} showLabel />
          */}
        </div>
      </div>
    </div>
  )
}
